import React from "react";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import styles from "./CoexistenceLimitationsModal.module.css";

/**
 * CoexistenceLimitationsModal — checklist das limitações da coexistência do
 * WhatsApp Business (Fase 4, Passo 2A) exibida ANTES de lançar o onboarding.
 *
 * Construída em cima do `ConfirmDialog` do design system (mesma caixa, overlay,
 * foco inicial no "Cancelar", fecha com ESC/overlay) — não introduz nova lib de
 * modal. A lista entra via `children` do ConfirmDialog.
 *
 * Linguagem coloquial pt-BR, sem jargão. Cores da marca herdadas das vars CSS.
 *
 * Props:
 *   isOpen     {boolean}      — controla a visibilidade.
 *   onConfirm  {() => void}   — chamado ao confirmar ("Entendi, conectar").
 *                              É aqui que o fluxo de FB.login deve disparar.
 *   onCancel   {() => void}   — chamado ao cancelar/fechar (ESC / overlay).
 *
 * TODO Fase 4: registrar aceite das limitações no backend (opcional).
 * TODO Fase 4 (2C): exibir badge/painel de progresso da coexistência.
 */
export default function CoexistenceLimitationsModal({
  isOpen,
  onConfirm,
  onCancel,
}) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Antes de conectar sua conta existente"
      confirmLabel="Entendi, conectar"
      cancelLabel="Agora não"
      confirmVariant="primary"
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <p className={styles.intro}>
        Conectar o número que você já usa no WhatsApp Business mantém ele ativo
        no seu celular e, ao mesmo tempo, ligado na nossa plataforma. Antes de
        seguir, dá uma olhada no que muda:
      </p>

      <ul className={styles.list}>
        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            Mantenha o <strong>WhatsApp Business aberto no celular</strong> —
            ficar muito tempo sem usar (~14 dias no aparelho principal, ~30 dias
            em aparelho secundário) <strong>desconecta</strong> a integração.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            A velocidade de envio fica fixa em{" "}
            <strong>20 mensagens por segundo</strong>.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            Alguns recursos de conversa 1:1 ficam <strong>desativados</strong>:
            mensagens temporárias, ver uma vez e localização em tempo real; as
            listas de transmissão ficam <strong>só leitura</strong>.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            Os <strong>grupos não são sincronizados</strong>.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            Na <strong>importação inicial (primeiras 24h)</strong>, deixar o app
            aberto ajuda a sincronizar o histórico das conversas.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            <strong>Custos:</strong> as mensagens enviadas pelo próprio app
            continuam <strong>grátis</strong>; as enviadas pela plataforma
            seguem o preço normal da Cloud API.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            É preciso ter o app{" "}
            <strong>WhatsApp Business 2.24.17 ou superior</strong>.
          </span>
        </li>

        <li className={styles.item}>
          <span className={styles.check} aria-hidden="true">
            ✓
          </span>
          <span>
            <strong>Para desconectar:</strong> dá pra fazer pelo próprio app, em{" "}
            <em>
              Configurações &gt; Conta &gt; Plataforma de Negócios &gt;
              Desconectar conta
            </em>
            .
          </span>
        </li>
      </ul>
    </ConfirmDialog>
  );
}
